import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";

import { Timeline } from "../../3d/";
import { generateFilterFunctionArray, useStore } from "../../store";
import {
  scales as generateScales,
  filterMulti,
  attributesFromAssociations,
  FILTER_EVENT_TYPES_THAT_ENABLE_CARPET_VIEW,
  CHART_DIMENSIONS,
  LAYOUT,
  filterInArray,
} from "../../utils";
import { makeNarrative } from "../../data/narrative";

const postProcessAttributes =
  (attributes) =>
  (acc, [key, modFunc]) =>
    acc.map((d) => {
      return {
        ...d,
        [key]: modFunc(d),
      };
    });

export const Visualization3D = ({ visible = true, scene = "narrative" }) => {
  const filters = useStore(useCallback((state) => state.filters, []));
  const actions = useStore(useCallback((state) => state.actions, []));
  const { updateTimelineAttributes } = actions;
  const debugPlatform = useStore(
    useCallback((state) => state.app.debugPlatform, [])
  );
  const dispatch = useStore(useCallback((state) => state.dispatch, []));
  const onToggleTopViewCallback = useCallback(
    (topView) =>
      dispatch({
        type: `TIMELINE_UPDATE_SETTINGS`,
        payload: {
          topView: topView,
        },
      }),
    [dispatch]
  );

  // This refers to all events data
  const data = useStore(useCallback((state) => state.data.events, []));
  const targetOrder = useStore(useCallback((state) => state.data.targets, []));
  const countries = useStore(useCallback((state) => state.data.countries, []));

  // ONBOARDING SECTION
  const showOnboarding = useStore(
    useCallback((state) => state.app.explore.showOnboarding, [])
  );
  const onboardingIdx = useStore(
    useCallback((state) => state.app.explore.onboardingIdx, [])
  );

  const {
    orthographic,
    topView,
    scales: { width: scaleWidth },
  } = useStore(useCallback((state) => state.app.timeline, []));

  const narrativeData = useMemo(() => makeNarrative({ data }), [data]);

  // Scales are generated from GLOBAL DATA (once)
  const { scales, associations, attributes } = useMemo(() => {
    console.log("memoizing scales, associations, attributes");
    const { scales, associations, fieldsByTarget } = generateScales(data, {
      yOrder: targetOrder,
      associations: new Set(countries),
      globalTime: {
        start: LAYOUT.TIMELINE.FILTERS.TIME.START,
        end: LAYOUT.TIMELINE.FILTERS.TIME.END,
      },
      width: CHART_DIMENSIONS.X.MAX,
    });
    const attributes = attributesFromAssociations({
      associations,
      data,
      defaultWidth: {
        targeted: LAYOUT.TIMELINE.SPHERES.SIZE,
        global: LAYOUT.TIMELINE.LINES.GLOBAL.WIDTH,
        contextual: LAYOUT.TIMELINE.LINES.CONTEXTUAL.WIDTH,
      },
      scales,
      fieldsByTarget,
    });
    // sort for depth: back-to-front (hind elements render first)
    attributes.sort((a, b) => a.z - b.z);
    return { scales, associations, attributes };
  }, [data, targetOrder, countries]);

  const filterFunctionArray = useMemo(() => {
    return generateFilterFunctionArray(filters, { filterAttributes: true });
  }, [filters]);

  // Memoize all attributes for all scenes only once
  const cachedNarrativeAttributes = useMemo(() => {
    console.log("memoizing cachedNarrativeAttributes");
    return narrativeData.map((nd) => {
      return nd.attributeModifiers.reduce(
        postProcessAttributes(attributes),
        filterMulti(attributes, ...nd.filters)
      );
    });
  }, [attributes, narrativeData]);

  const filteredAttributes = useMemo(() => {
    if (debugPlatform) {
      return cachedNarrativeAttributes[cachedNarrativeAttributes.length - 1];
    }
    if (scene === "empty") {
      // return attributes.slice(0, soundIdx);
      return [];
    } else {
      if (showOnboarding) {
        return cachedNarrativeAttributes[onboardingIdx];
      } else {
        console.log("refiltering occurred: filtering attributes");

        // Attributes are generated every refiltering
        const f = filterFunctionArray;

        // for further control over already filtered attributes
        // modifying based on a key/lambda array
        let filteredAttributes = narrativeData[
          onboardingIdx
        ].attributeModifiers.reduce(
          postProcessAttributes(attributes),
          filterMulti(attributes, ...f)
        );

        // send filteredAttributes to store, so they
        // can be used in card stack
        updateTimelineAttributes(filteredAttributes);
        return filteredAttributes;
      }
    }
  }, [
    attributes,
    debugPlatform,
    filterFunctionArray,
    cachedNarrativeAttributes,
    narrativeData,
    onboardingIdx,
    scene,
    showOnboarding,
    updateTimelineAttributes,
  ]);

  const timelineControls = {
    ...narrativeData[onboardingIdx].timeline,
  };
  const transitionConfig = {
    ...narrativeData[onboardingIdx].transitionConfig,
  };

  return (
    <>
      {true && (
        <Timeline
          associations={associations}
          attributes={attributes}
          filteredAttributes={filteredAttributes}
          centerMeshAroundOrigin={true}
          enablePan={!showOnboarding}
          enableZoom={!showOnboarding && !orthographic}
          enableRotate={!showOnboarding && !orthographic}
          materialUniforms={{
            isBreathing: false,
            isPulsating: false,
          }}
          onAssociationClick={
            showOnboarding ? () => {} : actions.onAssociationClick
          }
          onTimelineFocus={showOnboarding ? () => {} : actions.onTimelineFocus}
          onToggleTopView={onToggleTopViewCallback}
          orthographic={showOnboarding ? false : orthographic}
          resetOrbitControls={showOnboarding} // reset orbit controls when returning to onboarding
          rotateCarpetAssociationText={[-Math.PI / 4, 0, 0]}
          scales={scales}
          scaleTimelineWidth={
            showOnboarding
              ? 1
              : orthographic
              ? scaleWidth / CHART_DIMENSIONS.X.MAX
              : 1
          }
          showAssociationPlanes={false}
          showGridLinesTargets={!showOnboarding}
          showGridLinesFloorX={!showOnboarding}
          showGridLinesFloorZ={true}
          showGlobal={
            showOnboarding
              ? false
              : filterInArray(
                  filters.EVENT_TYPE.selected,
                  (d) => d
                )(FILTER_EVENT_TYPES_THAT_ENABLE_CARPET_VIEW)
          }
          showDates={true}
          showTargets={true}
          showTargetNames={filteredAttributes?.length !== attributes.length}
          translateX={!showOnboarding ? CHART_DIMENSIONS.X.MAX / 5 : 0}
          translateY={-7}
          // translateYOrthographic={-8.5}
          translateYCarpetView={3.5}
          topView={topView}
          zoomToData={false}
          zoomOnMesh={1}
          transitionConfig={showOnboarding ? transitionConfig : {}}
          {...(showOnboarding ? timelineControls : {})}
          visible={visible}
        />
      )}
    </>
  );
};

Visualization3D.propTypes = {
  scene: PropTypes.oneOf(["empty", "narrative"]),
  visible: PropTypes.bool.isRequired,
};
